import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { getFollowStatus, followFestival, unfollowFestival } from "@/api/client";
import { Heart } from "lucide-react";

export function FollowButton({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data: status } = useQuery({
    queryKey: ["follow-status", slug],
    queryFn: () => getFollowStatus(slug),
    enabled: !!slug,
  });

  const followMutation = useMutation({
    mutationFn: () => followFestival(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", slug] });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: () => unfollowFestival(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", slug] });
    },
  });

  const isFollowing = status?.is_following ?? false;
  const followerCount = status?.follower_count ?? 0;
  const isLoading = followMutation.isPending || unfollowMutation.isPending;

  const handleClick = () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (isFollowing) {
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${
        isFollowing
          ? "border-accent/40 bg-accent/10 text-accent hover:bg-accent/20"
          : "border-line text-ink-3 hover:bg-panel-2 hover:text-ink"
      }`}
    >
      <Heart
        className={`h-4 w-4 ${isFollowing ? "fill-accent" : ""}`}
      />
      {isFollowing ? "Following" : "Follow"}
      {followerCount > 0 && (
        <span className="ml-0.5 text-xs text-ink-3">{followerCount}</span>
      )}
    </button>
  );
}
